import {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";

import { TOKEN_STORAGE_KEY } from "@/api/client";

export type Role = "user" | "operation" | "admin";

export interface AuthState {
  userId: string | null;
  email: string | null;
  role: Role | null;
  token: string | null;
}

export interface AuthContextValue extends AuthState {
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const USER_STORAGE_KEY = "quantica.backoffice.user";

const Ctx = createContext<AuthContextValue | null>(null);

function readPersistedState(): AuthState {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const userJson = localStorage.getItem(USER_STORAGE_KEY);
  if (!token || !userJson) {
    return { userId: null, email: null, role: null, token: null };
  }
  try {
    const u = JSON.parse(userJson) as {
      userId: string;
      email: string;
      role: Role;
    };
    return { userId: u.userId, email: u.email, role: u.role, token };
  } catch {
    // Stato persistito corrotto: pulizia best-effort, ritorno logged-out.
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    return { userId: null, email: null, role: null, token: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(readPersistedState);

  async function login(email: string, password: string) {
    const res = await fetch("/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      // Tenta di estrarre il messaggio di errore dal body (ProblemDetails / JSON
      // generico). Se non c'è, ripiega sullo status code.
      let detail: string | null = null;
      try {
        const body = await res.json();
        detail = body.detail ?? body.message ?? null;
      } catch {
        /* body non JSON: ignora */
      }
      throw new Error(
        detail ?? `Accesso fallito (${res.status})`,
      );
    }
    const data = (await res.json()) as {
      user_id: string;
      session_token: string;
      role: Role;
    };
    if (data.role !== "operation" && data.role !== "admin") {
      throw new Error(
        "Solo gli account operation o admin possono accedere al backoffice",
      );
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, data.session_token);
    localStorage.setItem(
      USER_STORAGE_KEY,
      JSON.stringify({ userId: data.user_id, email, role: data.role }),
    );
    setState({
      userId: data.user_id,
      email,
      role: data.role,
      token: data.session_token,
    });
  }

  async function logout() {
    const token = state.token;
    if (token) {
      try {
        await fetch("/v1/auth/logout", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Best-effort: il backend può essere irraggiungibile, ma a livello UI
        // dobbiamo comunque ripulire la sessione locale.
      }
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setState({ userId: null, email: null, role: null, token: null });
  }

  return (
    <Ctx.Provider value={{ ...state, login, logout }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth() chiamato fuori da <AuthProvider>");
  return v;
}
